import React, { FC } from 'react';
import { useTranslation } from 'next-i18next';
import type { LandingResults, Title } from 'src/common/types/api/v4/search/v1/landing';
import type { PageDataFromBreadCrumbs } from 'src/search/utils/getPageDataFromBreadcrumbs';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { NO_FILTERS_PARAM, PAGE_PARAM, PATHS } from 'src/common/constants';
import Carousel from 'src/common/components/dockyard/Carousel';
import Typography from 'src/common/components/Typography';
import Button from 'src/common/components/ButtonDS22';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import BoatTypeCard from 'src/common/components/BoatTypeCard';
import DlpLayout from 'src/search/components/DlpLayout';
import CarouselButtons from 'src/common/components/dockyard/CarouselButtons';
import { useSearchInputContext } from 'src/common/components/Search/SearchInputProvider';
import { formatDate } from 'src/common/utils/dateHelpers';
import styles from './DlpBoatTypes.module.scss';

export type DlpBoatTypesProps = {
  title: Title;
  listingTypes: LandingResults['listing_types'];
} & Pick<PageDataFromBreadCrumbs, 'locationSlug'>;

const DlpBoatTypes: FC<DlpBoatTypesProps> = ({
  title,
  listingTypes,
  locationSlug,
}) => {
  const [t] = useTranslation('discovery-landing-pages');
  const { currency: { symbol } } = useCurrencyContext();
  const { filterDates } = useSearchInputContext();
  const searchDates = filterDates?.map((date) => formatDate(date));

  const boatTypes = listingTypes?.map((category) => {
    const slugIsolated = category.slug.split('/')[0];
    const min = category.price?.min;
    const max = category.price?.max;
    const href = () => {
      const location = locationSlug ? `/${locationSlug}` : '';
      return `${PATHS.SEARCH}${slugIsolated}${location}`;
    };

    const minFormatted = `${symbol}${min?.toLocaleString()}`;
    const maxFormatted = `${symbol}${max?.toLocaleString()}`;
    const price = min === max ? minFormatted : `${minFormatted}-${maxFormatted}`;
    return {
      price,
      href: href(),
      categorySlug: slugIsolated,
    };
  });

  const viewAllButtonHref = () => {
    const pageLocation = locationSlug ? `${locationSlug}` : '';
    const datesParam = searchDates?.length ? `&dates=${searchDates.join(',')}` : '';
    return `${PATHS.SEARCH}${pageLocation}${PAGE_PARAM}&${NO_FILTERS_PARAM}&${datesParam}`;
  };

  return (
    <DlpLayout backgroundColor="midnight02">
      <div className={styles.root}>
        <CarouselProvider>
          <div className={styles.header}>
            <Typography
              type="h3"
              size="heading-3"
              weight="semibold"
            >
              {title}
            </Typography>
            <div className={styles.buttonsDesktop}>
              <CarouselButtons
                spacing="together"
                variant="primary-dark"
              />
            </div>
          </div>
          <Carousel>
            {boatTypes?.map((boat) => (
              <BoatTypeCard
                key={boat.categorySlug}
                urlSegment={boat.categorySlug}
                href={boat.href}
                ctaText={boat.price}
              />
            ))}
          </Carousel>
        </CarouselProvider>
        <div className={styles.button}>
          <Button
            type="link"
            href={viewAllButtonHref()}
            variant="tertiary"
            size="medium"
          >
            {t('boat-types--cta')}
          </Button>
        </div>
      </div>
    </DlpLayout>
  );
};

export default DlpBoatTypes;
